<template>
  <AppCard
    :accent="accentColor"
    no-padding
    :class="['shadow-md', { 'opacity-75': isCancelled }]"
  >
    <div class="px-7 pt-6 pb-5">
      <!-- Top row: identity + actions -->
      <div class="flex items-start justify-between gap-6">
        <div>
          <div class="flex items-center gap-3">
            <h1
              :class="[
                'text-[22px] leading-tight font-bold tracking-tight',
                isCancelled ? 'text-neutral-400' : 'text-navy-900',
              ]"
            >
              {{ order.displayHeadline }}
            </h1>
            <StatusBadge :variant="getStatusDisplay(order.status).variant" dot>
              {{ getStatusDisplay(order.status).label }}
            </StatusBadge>
          </div>
          <p
            v-if="order.displaySubline"
            :class="[
              'mt-1 text-sm',
              isCancelled ? 'text-neutral-300' : 'text-navy-400',
            ]"
          >
            {{ order.displaySubline }}
          </p>
          <p
            v-if="descriptionBody"
            :class="[
              'mt-1 text-sm whitespace-pre-line',
              isCancelled ? 'text-neutral-300' : 'text-navy-600',
            ]"
          >
            {{ descriptionBody }}
          </p>
        </div>

        <div class="flex shrink-0 items-center gap-2.5">
          <!-- Processing spinner -->
          <div
            v-if="spinnerLabel"
            class="flex items-center gap-2 rounded-lg bg-violet-50 px-4 py-2.5 text-sm font-medium text-violet-700 ring-1 ring-violet-200"
          >
            <i class="pi pi-spinner pi-spin text-sm" />
            {{ spinnerLabel }}
          </div>

          <!-- Push completed badge -->
          <div
            v-if="order.pushStatus === 'completed'"
            class="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700 ring-1 ring-emerald-200"
          >
            <i class="pi pi-check-circle text-sm" />
            Pushed to {{ agentName }}
          </div>

          <!-- Push failed warning -->
          <div
            v-if="order.pushStatus === 'failed'"
            class="flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700 ring-1 ring-amber-200"
          >
            <i class="pi pi-exclamation-triangle text-sm" />
            {{ order.pushError || "Push failed" }}
          </div>

          <!-- Primary CTA -->
          <AppButton
            v-if="ctaConfig"
            :variant="ctaConfig.variant"
            @click="handleCtaClick"
          >
            {{ ctaConfig.label }}
          </AppButton>

          <!-- Kebab menu -->
          <AppButton
            v-if="!isCancelled"
            variant="secondary"
            size="sm"
            icon="pi pi-ellipsis-v"
            @click="toggleMenu"
          />
          <Menu ref="menuRef" :model="menuItems" :popup="true" />
        </div>
      </div>

      <!-- Meta row -->
      <div
        :class="[
          'mt-5 flex items-center gap-6 border-t pt-4',
          isCancelled ? 'border-neutral-100' : 'border-navy-100',
        ]"
      >
        <div v-if="order.externalFileNumber" class="flex flex-col gap-0.5">
          <span
            class="text-navy-400 text-[11px] font-semibold tracking-wide uppercase"
          >
            File Number
          </span>
          <span class="text-navy-800 text-sm font-medium">
            {{ order.externalFileNumber }}
          </span>
        </div>

        <div v-if="order.externalFileNumber" class="bg-navy-100 h-8 w-px" />

        <div class="flex flex-col gap-0.5">
          <span
            class="text-navy-400 text-[11px] font-semibold tracking-wide uppercase"
          >
            Officer
          </span>
          <span
            class="text-navy-800 flex items-center gap-2 text-sm font-medium"
          >
            <span
              class="bg-navy-700 flex h-[22px] w-[22px] items-center justify-center rounded-full text-[9px] font-bold tracking-wide text-white"
            >
              {{ officerInitials }}
            </span>
            {{ order.officer.name }}
          </span>
        </div>

        <div class="bg-navy-100 h-8 w-px" />

        <div class="flex flex-col gap-0.5">
          <span
            class="text-navy-400 text-[11px] font-semibold tracking-wide uppercase"
          >
            Verification
          </span>
          <span
            v-if="order.verificationEnabled"
            class="flex items-center gap-1.5 text-sm font-semibold text-amber-600"
          >
            <i class="pi pi-star-fill text-xs text-amber-500" />
            Required
          </span>
          <span v-else class="text-navy-400 text-sm"> Not required </span>
        </div>

        <div class="bg-navy-100 h-8 w-px" />

        <div class="flex flex-col gap-0.5">
          <span
            class="text-navy-400 text-[11px] font-semibold tracking-wide uppercase"
          >
            Document Email
          </span>
          <span
            v-if="order.documentEmailEnabled"
            class="flex items-center gap-1.5 text-sm font-semibold text-emerald-600"
          >
            <i class="pi pi-envelope text-xs text-emerald-500" />
            Enabled
          </span>
          <span v-else class="text-navy-400 text-sm"> Disabled </span>
        </div>

        <template v-if="order.importAgentRegistration">
          <div class="bg-navy-100 h-8 w-px" />
          <div class="flex flex-col gap-0.5">
            <span
              class="text-navy-400 text-[11px] font-semibold tracking-wide uppercase"
            >
              Source
            </span>
            <span
              class="flex items-center gap-1.5 text-sm font-medium text-blue-600"
            >
              <i class="pi pi-cloud-download text-xs text-blue-500" />
              {{ order.importAgentRegistration.name || "Agent" }}
            </span>
          </div>
        </template>
      </div>
    </div>
  </AppCard>
</template>

<script setup lang="ts">
import Menu from "primevue/menu"
import { computed, ref } from "vue"

import { useOrderStatus } from "../../composables/useOrderStatus"
import type { OrderType } from "../../pages/Order/types"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"
import StatusBadge from "../ui/StatusBadge.vue"

const { getStatusDisplay } = useOrderStatus()

interface Props {
  order: OrderType
  hasDocuments?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hasDocuments: false,
})

const emit = defineEmits<{
  connectTable: []
  cancelClosing: []
  exportAll: []
  edit: []
  delete: []
  downloadOriginals: []
  downloadSigned: []
  downloadClosingReport: []
  pushToAgent: []
  cancelPush: []
}>()

const menuRef = ref()

const descriptionBody = computed(() => {
  if (!props.order.description) return null
  const lines = props.order.description.split("\n")
  const rest = lines.slice(2).join("\n").trim()
  return rest || null
})

const isCancelled = computed(() => props.order.status === "CANCELED")

const agentName = computed(
  () => props.order.importAgentRegistration?.name || "Agent",
)

const officerInitials = computed(() =>
  props.order.officer.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2),
)

const accentColor = computed(() => {
  const colors: Record<string, string> = {
    DRAFT: "bg-amber-400",
    CLOSING: "bg-blue-500",
    UPLOADING: "bg-violet-500",
    FINALIZING: "bg-violet-500",
    COMPLETED: "bg-emerald-500",
    CANCELLED: "bg-neutral-400",
  }
  return colors[props.order.status]
})

const isPushing = computed(() =>
  ["pending", "in_progress"].includes(props.order.pushStatus ?? ""),
)

const spinnerLabel = computed(() => {
  if (props.order.pushStatus === "pending") return "Queuing push..."
  if (props.order.pushStatus === "in_progress")
    return `Pushing to ${agentName.value}...`

  const spinners: Record<string, string> = {
    UPLOADING: "Uploading documents...",
    FINALIZING: "Finalizing...",
  }
  return spinners[props.order.status] ?? null
})

type ButtonVariant = "primary" | "secondary" | "danger"

interface CtaConfig {
  label: string
  variant: ButtonVariant
  event:
    | "connectTable"
    | "cancelClosing"
    | "exportAll"
    | "pushToAgent"
    | "cancelPush"
}

const ctaConfig = computed<CtaConfig | null>(() => {
  const status = props.order.status

  if (status === "DRAFT") {
    return {
      label: "Connect to Table",
      variant: "primary",
      event: "connectTable",
    }
  }
  if (status === "CLOSING") {
    return {
      label: "Cancel Closing",
      variant: "danger",
      event: "cancelClosing",
    }
  }
  if (status === "COMPLETED") {
    return completedCtaConfig.value
  }
  return null
})

const completedCtaConfig = computed<CtaConfig | null>(() => {
  const ps = props.order.pushStatus

  if (isPushing.value) {
    return { label: "Cancel Push", variant: "danger", event: "cancelPush" }
  }
  if (ps === "failed") {
    return {
      label: `Send to ${agentName.value}`,
      variant: "primary",
      event: "pushToAgent",
    }
  }
  if (props.order.pushable && ps !== "completed") {
    return {
      label: `Send to ${agentName.value}`,
      variant: "primary",
      event: "pushToAgent",
    }
  }
  return {
    label: "Export All Documents",
    variant: "primary",
    event: "exportAll",
  }
})

const handleCtaClick = () => {
  if (!ctaConfig.value) return
  const event = ctaConfig.value.event
  if (event === "connectTable") emit("connectTable")
  else if (event === "cancelClosing") emit("cancelClosing")
  else if (event === "exportAll") emit("exportAll")
  else if (event === "pushToAgent") emit("pushToAgent")
  else if (event === "cancelPush") emit("cancelPush")
}

const toggleMenu = (event: Event) => {
  menuRef.value.toggle(event)
}

const menuItems = computed(() => {
  const status = props.order.status
  const items: object[] = []

  if (status === "DRAFT") {
    items.push({
      label: "Edit",
      icon: "pi pi-pen-to-square",
      command: () => emit("edit"),
    })
    items.push({
      label: "Delete",
      icon: "pi pi-trash",
      class: "text-red-600",
      command: () => emit("delete"),
    })
    return items
  }

  items.push({
    label: "Original",
    icon: "pi pi-download",
    disabled: !props.hasDocuments,
    command: () => emit("downloadOriginals"),
  })

  if (status === "COMPLETED") {
    items.push({
      label: "Signed",
      icon: "pi pi-download",
      command: () => emit("downloadSigned"),
    })
    items.push({
      label: "Closing Report",
      icon: "pi pi-file-pdf",
      command: () => emit("downloadClosingReport"),
    })

    // Add push option in menu when not yet pushed
    if (
      props.order.pushable &&
      !isPushing.value &&
      props.order.pushStatus !== "completed"
    ) {
      const pushLabel = `Send to ${agentName.value}`
      items.push({
        label: pushLabel,
        icon: "pi pi-cloud-upload",
        command: () => emit("pushToAgent"),
      })
    }

    items.push({ separator: true })
    items.push({
      label: "Delete",
      icon: "pi pi-trash",
      class: "text-red-600",
      command: () => emit("delete"),
    })
  }

  return items
})
</script>
